import { useCallback, useMemo, useState } from "react"
import { AppBar, Box, Button, IconButton, Typography, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery, Divider } from "@mui/material"
import { Diamond, Phone, WhatsApp, Email, Language } from "@mui/icons-material"
import { useTheme } from "@mui/material/styles"
import { CONTACT_EMAIL, CONTACT_PHONE, RAPNET_URL } from "../constants"

const navContainerSx = {
    paddingX: { sm: 8, xs: 2 },
    paddingY: 2,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
}

const brandLinkSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:hover': {
        opacity: 0.8
    }
}

const desktopActionsSx = {
    display: { sm: 'block', xs: 'none' },
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
}

const formatPhoneDisplay = (value) => {
    if (!value) return ""
    const hasPlus = value.startsWith("+")
    const digits = value.replace(/\D/g, "")
    if (digits.length <= 4) return `${hasPlus ? "+" : ""}${digits}`

    const last4 = digits.slice(-4)
    const head = digits.slice(0, -4)
    const headGroups = head.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    return `${hasPlus ? "+" : ""}${headGroups} ${last4}`.trim()
}

export function Navbar() {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [contactUsButtonAnchor, setContactUsButtonAnchor] = useState(null)
    const menuOpen = useMemo(() => Boolean(contactUsButtonAnchor), [contactUsButtonAnchor])
    const whatsappUrl = `https://wa.me/${CONTACT_PHONE?.replace(/^\+/, "") || ""}`
    const emailUrl = `mailto:${CONTACT_EMAIL || ""}`
    const contactPhoneDisplay = formatPhoneDisplay(CONTACT_PHONE)

    const handleOpenMenu = useCallback((event) => {
        setContactUsButtonAnchor(event.currentTarget)
    }, [])

    const handleCloseMenu = useCallback(() => {
        setContactUsButtonAnchor(null)
    }, [])

    return (
        <AppBar position="static">
            <Box sx={navContainerSx}>
                <Box
                    component="a"
                    href="/"
                    sx={brandLinkSx}
                >
                    <Diamond />
                    <Typography
                        noWrap
                        sx={{
                            fontSize: { sm: 20, xs: 14 },
                            fontWeight: 700,
                            letterSpacing: { sm: 7.15, xs: 4.65 },
                            marginLeft: 2,
                        }}
                    >
                        BOTHRA ENTERPRISES
                    </Typography>
                </Box>

                <Box sx={desktopActionsSx}>
                    <Button color="inherit" href={RAPNET_URL} target="_blank" rel="noopener noreferrer">
                        RAPNET
                    </Button>
                    <Button color="inherit" onClick={handleOpenMenu}>
                        CONTACT US
                    </Button>
                </Box>

                <IconButton
                    sx={{ display: { sm: 'none', xs: 'block' } }}
                    color="inherit"
                    onClick={handleOpenMenu}
                >
                    <Phone />
                </IconButton>

                <Menu
                    anchorEl={contactUsButtonAnchor}
                    open={menuOpen}
                    onClose={handleCloseMenu}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    {isMobile && (
                        <Box>
                            <MenuItem href={RAPNET_URL} target="_blank" rel="noopener noreferrer" component="a">
                                <ListItemIcon><Language fontSize="small" /></ListItemIcon>
                                <ListItemText
                                    primary="Rapnet"
                                    secondary={RAPNET_URL}
                                    secondaryTypographyProps={{ sx: { letterSpacing: 0.2 } }}
                                />
                            </MenuItem>
                            <Divider />
                        </Box>
                    )}
                    <MenuItem href={`tel:${CONTACT_PHONE}`} target="_blank" rel="noopener noreferrer" component="a">
                        <ListItemIcon><Phone fontSize="small" /></ListItemIcon>
                        <ListItemText
                            primary="Call"
                            secondary={contactPhoneDisplay}
                            secondaryTypographyProps={{ sx: { letterSpacing: 0.2 } }}
                        />
                    </MenuItem>
                    <MenuItem href={whatsappUrl} target="_blank" rel="noopener noreferrer" component="a">
                        <ListItemIcon><WhatsApp fontSize="small" /></ListItemIcon>
                        <ListItemText
                            primary="WhatsApp"
                            secondary={contactPhoneDisplay}
                            secondaryTypographyProps={{ sx: { letterSpacing: 0.2 } }}
                        />
                    </MenuItem>
                    <MenuItem href={emailUrl} target="_blank" rel="noopener noreferrer" component="a">
                        <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                        <ListItemText
                            primary="Email"
                            secondary={CONTACT_EMAIL}
                            secondaryTypographyProps={{ sx: { letterSpacing: 0.2 } }}
                        />
                    </MenuItem>
                </Menu>
            </Box>
        </AppBar>
    )
}
